import { useState } from 'react';
import { Alert, Button, Divider, Flex, Form, Input, Typography, theme } from 'antd';
import { Link, useNavigate } from 'react-router';
import {
  ContinuarComGoogle,
  lembrarFormaDeEntrada,
  MENSAGEM_DE_FALHA_INESPERADA,
  mensagemDeErro,
  TituloDaTela,
} from '@titularis/ui';
import { authClient, signUp } from '../../auth';
import { enderecoDaPrivacidade, enderecoDosTermos } from '../../site';
import { comDestino, useDestino, veioComprar } from './destino';
import { enderecoDaVerificacao, retornoDoEmail } from './verificacao';

interface Campos {
  name: string;
  email: string;
  password: string;
}

/**
 * O cadastro.
 *
 * Mesma construção da tela de entrada, e não por economia: as duas são a mesma
 * porta vista de dois lados, e quem troca de uma para a outra no meio do
 * caminho - o que acontece o tempo todo, porque ninguém lembra se já se
 * cadastrou - não pode ter a impressão de ter mudado de site.
 *
 * O botão do Google aparece aqui também, com o mesmo rótulo: ele cria a conta
 * sem senha para inventar nem e-mail para confirmar, que é o caminho mais curto
 * até a primeira questão.
 */
export function CriarContaPage() {
  const { token } = theme.useToken();
  const destino = useDestino();
  const navigate = useNavigate();
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const criar = async ({ name, email, password }: Campos) => {
    setErro(null);
    setEnviando(true);

    try {
      const { error } = await signUp.email({
        name,
        email,
        password,
        /*
         * Para onde o link do e-mail devolve a pessoa. Sem isto, quem veio
         * comprar confirmava a conta e caía na Home, com a compra esquecida no
         * meio do caminho - o servidor sobrescrevia o retorno por `/`.
         */
        callbackURL: retornoDoEmail(destino),
      });

      if (error) {
        setErro(mensagemDeErro(error, 'Não foi possível criar a conta. Tente novamente.'));
        return;
      }

      /*
       * A porta usada fica lembrada já no cadastro, e não só no primeiro login.
       * Sem isto, quem criava a conta por e-mail voltava dias depois e via a
       * etiqueta "Da última vez" no botão do Google - a marca de alguma sessão
       * anterior naquele navegador, apontando para a porta errada.
       */
      lembrarFormaDeEntrada('email');

      /*
       * Navega, e não guarda em estado.
       *
       * O `signUp` dispara a revalidação da sessão, e enquanto ela corre o
       * guarda desta rota troca a tela inteira por um spinner - o que desmonta
       * esta página e apagaria qualquer coisa guardada aqui dentro. Medido em
       * 17/09/2026: a confirmação viveu 5 milissegundos. O endereço sobrevive
       * a isso, e ao botão de voltar (D-1).
       */
      void navigate(enderecoDaVerificacao(email, destino), { replace: true });
    } catch {
      // Sem este catch, uma falha de rede deixava a tela exatamente como estava:
      // nem erro, nem confirmação, nem fim do estado de carregando.
      setErro(MENSAGEM_DE_FALHA_INESPERADA);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Flex vertical gap={token.margin}>
      {/*
        Quem chegou de "Comprar o Enac" lia "Comece a estudar hoje", e nada
        dizia que havia uma compra em curso - o cadastro parecia um desvio em
        vez de um passo. O destino que veio no endereço é o que sabe disso.
      */}
      <TituloDaTela
        titulo={veioComprar(destino) ? 'Falta só a conta' : 'Comece a estudar hoje'}
        descricao={
          veioComprar(destino)
            ? 'Crie sua conta para concluir a compra. É rápido, e você continua de onde parou.'
            : 'Crie sua conta para resolver questões com comentário de verdade.'
        }
      />

      {erro ? <Alert type="error" showIcon title={erro} /> : null}

      <ContinuarComGoogle
        authClient={authClient}
        destino={destino}
        aoFalhar={(mensagem) => setErro(mensagem || null)}
      />

      <Divider plain style={{ margin: 0 }}>
        <Typography.Text type="secondary">ou</Typography.Text>
      </Divider>

      <Form<Campos> layout="vertical" onFinish={criar} requiredMark={false} disabled={enviando}>
        <Form.Item
          name="name"
          label="Nome"
          rules={[{ required: true, message: 'Informe seu nome.' }]}
        >
          <Input autoComplete="name" size="large" />
        </Form.Item>

        <Form.Item
          name="email"
          label="E-mail"
          rules={[
            {
              required: true,
              type: 'email',
              message: 'Informe um e-mail válido.',
            },
          ]}
        >
          <Input autoComplete="email" inputMode="email" size="large" />
        </Form.Item>

        <Form.Item
          name="password"
          label="Senha"
          rules={[
            { required: true, message: 'Escolha uma senha.' },
            { min: 8, message: 'Use pelo menos 8 caracteres.' },
          ]}
        >
          <Input.Password autoComplete="new-password" size="large" />
        </Form.Item>

        <Button type="primary" htmlType="submit" size="large" block loading={enviando}>
          Criar conta
        </Button>
      </Form>

      {/*
        O aceite fica abaixo das duas formas de entrar, e não dentro do
        formulário: quem cria conta pelo Google nunca passa pelo `Form`, e um
        aviso preso a ele valeria para metade das contas.

        Sem caixa de marcar, de propósito. O aceite por uso é o que o Marco
        Civil e o CDC admitem para termos apresentados antes do ato, e uma caixa
        a mais entre a pessoa e a conta gratuita custaria cadastro sem
        acrescentar prova - o que prova é o texto estar visível aqui.
      */}
      <Typography.Text type="secondary" style={{ textAlign: 'center', fontSize: 12 }}>
        Ao criar conta, você concorda com os{' '}
        <Typography.Link href={enderecoDosTermos} target="_blank" rel="noreferrer">
          Termos de Uso
        </Typography.Link>{' '}
        e com a{' '}
        <Typography.Link href={enderecoDaPrivacidade} target="_blank" rel="noreferrer">
          Política de Privacidade
        </Typography.Link>
        .
      </Typography.Text>

      <Typography.Text type="secondary" style={{ textAlign: 'center' }}>
        Já tem conta? <Link to={comDestino('/entrar', destino)}>Entrar</Link>
      </Typography.Text>
    </Flex>
  );
}
